import { confirmSufficientFunds, ledgerMoney } from 'components/TrustLedgerGrid'
import type { ITrustAccount } from 'components/TrustLedgerGrid/types'
import { useEffect, useState } from 'react'
import { getTrustAccountLedger } from 'services'
import { Modal } from 'stories/components'

/**
 * Move money between two trust accounts. The backend writes both halves at once and ties them
 * together, so neither account can end up reconciled against a half-recorded move.
 */
export default function TrustAccountTransferModal({
  accounts,
  onSubmit,
  onClose,
}: {
  accounts: ITrustAccount[]
  onSubmit: (data: Record<string, any>) => Promise<any>
  onClose: (changed: boolean) => void
}) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [fromBalance, setFromBalance] = useState<number | null>(null)
  const [values, setValues] = useState({
    fromTrustAccountId: accounts[0]?.id || 0,
    toTrustAccountId: accounts[1]?.id || 0,
    date: new Date().toISOString().slice(0, 10),
    amount: '',
    reference: '',
    memo: '',
  })

  const set = (key: string, value: any) => setValues((prev) => ({ ...prev, [key]: value }))

  const fromAccount = accounts.find((account) => account.id === Number(values.fromTrustAccountId))

  /**
   * What the account being drawn from holds. Unlike the entry modal there is no ledger on screen
   * to read it off - this modal opens from the account list and picks its own From account - so it
   * is fetched per account, and shown, not just checked: seeing the balance next to the picker is
   * what stops the transfer being entered wrong in the first place. `count: 1` because only the
   * balance is wanted; the backend strikes it over the whole account before it pages.
   */
  useEffect(() => {
    const accountId = Number(values.fromTrustAccountId)
    setFromBalance(null)
    if (!accountId) return
    let cancelled = false
    getTrustAccountLedger(accountId, { count: 1 })
      .then((res: any) => {
        if (cancelled) return
        const total = (res?.totals || []).find((item: any) => item.trustAccountId === accountId)
        setFromBalance(total ? Number(total.balance) : Number(res?.balance ?? 0))
      })
      // A balance we could not read must not block the transfer; it just goes unchecked.
      .catch(() => !cancelled && setFromBalance(null))
    return () => {
      cancelled = true
    }
  }, [values.fromTrustAccountId])

  const submit = async () => {
    setError('')
    const amount = Number(String(values.amount).replace(/,/g, ''))
    if (!amount) return setError('Enter an amount')
    // Both halves are written together, so the only account this can overdraw is the From one.
    const name = fromAccount?.name || 'This account'
    if (fromBalance !== null && !(await confirmSufficientFunds(amount, fromBalance, name))) return
    setLoading(true)
    try {
      await onSubmit({
        ...values,
        fromTrustAccountId: Number(values.fromTrustAccountId),
        toTrustAccountId: Number(values.toTrustAccountId),
        amount,
      })
      onClose(true)
    } catch (err: any) {
      setError(err?.message || 'Could not record the transfer')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal title="Transfer Between Trust Accounts" titleOkay="Transfer" loading={loading} onClose={() => onClose(false)} onOk={submit} isOpen>
      <div className="text-gray-900 w-144 grid grid-cols-2 gap-4 mb-3 mt-2">
        <div>
          <div className="text-[13px] text-gray-500 mb-1">From</div>
          <select
            className="text-[14px] border rounded px-2 py-2 w-full"
            value={values.fromTrustAccountId}
            onChange={(e) => set('fromTrustAccountId', e.target.value)}
          >
            {accounts.map((account) => (
              <option key={account.id} value={account.id}>
                {account.name}
              </option>
            ))}
          </select>
          {fromBalance !== null && (
            <div className="text-[13px] text-gray-500 mt-1">
              {/* Worded like the warning rather than shown as a minus figure, which reads as a typo. */}
              {fromBalance < 0 ? `Overdrawn by ${ledgerMoney(-1 * fromBalance)}` : `Holds ${ledgerMoney(fromBalance)}`}
            </div>
          )}
        </div>

        <div>
          <div className="text-[13px] text-gray-500 mb-1">To</div>
          <select
            className="text-[14px] border rounded px-2 py-2 w-full"
            value={values.toTrustAccountId}
            onChange={(e) => set('toTrustAccountId', e.target.value)}
          >
            {accounts.map((account) => (
              <option key={account.id} value={account.id}>
                {account.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <div className="text-[13px] text-gray-500 mb-1">Date</div>
          <input
            type="date"
            className="text-[14px] border rounded px-2 py-2 w-full"
            value={values.date}
            onChange={(e) => set('date', e.target.value)}
          />
        </div>

        <div>
          <div className="text-[13px] text-gray-500 mb-1">Amount</div>
          <input
            className="text-[14px] border rounded px-2 py-2 w-full"
            value={values.amount}
            onChange={(e) => set('amount', e.target.value)}
          />
        </div>

        <div>
          <div className="text-[13px] text-gray-500 mb-1">Reference</div>
          <input
            className="text-[14px] border rounded px-2 py-2 w-full"
            value={values.reference}
            onChange={(e) => set('reference', e.target.value)}
          />
        </div>

        <div>
          <div className="text-[13px] text-gray-500 mb-1">Memo</div>
          <input
            className="text-[14px] border rounded px-2 py-2 w-full"
            value={values.memo}
            onChange={(e) => set('memo', e.target.value)}
          />
        </div>

        {!!error && <div className="col-span-2 text-[13px] text-red-600">{error}</div>}
      </div>
    </Modal>
  )
}
